import { useCallback, useEffect, useState } from 'react';
import { UseFormReset } from 'react-hook-form';

import { DisplayModes } from 'services/webedi/types';

import { ProductMasterdataFormValues, ProductRow, PRODUCTS_DATA_PREFIX } from '../helpers/productRows';

export type ProductMasterdataMode = DisplayModes.view | DisplayModes.edit;

/**
 * Owns the view/edit toggle and keeps the page's RHF form in step with the loaded query rows.
 *
 * The table always reads from the form (via `useDataTableContext()`'s `useWatch` snapshot), not
 * from the query directly — so even in view mode the form's `products` must be kept current as
 * more pages load. This hook resets the form to the latest `rows` on every change *while in view
 * mode*; entering edit mode simply flips the mode without touching the form (the most recent
 * reset already seeded it), and Cancel calls a bare `reset()` back to those same defaults,
 * discarding whatever was typed. The reset is a no-op for `rows` once edit mode starts, because
 * infinite loading is disabled and the search input unmounts while editing (see
 * `ProductMasterdata.tsx`), so there is nothing new to catch up on — and syncing mid-edit would
 * blow away unsaved changes.
 */
export const useProductMasterdataMode = (
  reset: UseFormReset<ProductMasterdataFormValues>,
  rows: Array<ProductRow>,
  searchQuery: string,
) => {
  const [mode, setMode] = useState<ProductMasterdataMode>(DisplayModes.view);

  useEffect(() => {
    if (mode === DisplayModes.view) {
      reset({ [PRODUCTS_DATA_PREFIX]: rows });
    }
  }, [mode, reset, rows]);

  // The search *input* unmounts while editing, but `filter[search]` is a URL param — back/forward
  // navigation or a pasted URL can still change it. Left alone, that starts a new query (new
  // `isLoading`) without ever leaving edit mode, so the table would keep showing the old search's
  // rows against a toolbar/URL that already say something else. Falling back to view mode on any
  // search change is exactly Cancel's discard behavior, and it's safe to run unconditionally: this
  // only fires when `searchQuery` itself changes, and changing it from view mode (the only way a
  // user can drive it directly, since the input doesn't exist while editing) just re-sets a mode
  // we're already in.
  useEffect(() => {
    setMode(DisplayModes.view);
  }, [searchQuery]);

  const enterEditMode = useCallback(() => setMode(DisplayModes.edit), []);

  const cancelEdit = useCallback(() => {
    reset();
    setMode(DisplayModes.view);
  }, [reset]);

  /** Called once a save has succeeded and the query has been invalidated+refetched. */
  const finishEdit = useCallback(() => setMode(DisplayModes.view), []);

  return { mode, enterEditMode, cancelEdit, finishEdit };
};
